// ======================================================================
// 📁 File: src/modules/ppe/pages/engineering/boq/BOQMasterList.jsx
// ======================================================================
// PURPOSE:
//   - BOQ Master list page
//   - UI is 100% identical to RoutingListPage.jsx
//   - Only fields, APIs and labels are changed
//
// IMPORTANT:
//   - This file DOES NOT touch Order BOQ logic
//   - This file works only with IPE BOM MASTER tables
// ======================================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../../api/axiosClient";

//import BOQKpiBar from "./BOQKpiBar";
import BOMKpiBar from "./BOMKpiBar";
export default function BOQMasterList() {
  const navigate = useNavigate();

  // ------------------------------------------------------------------
  // STATE
  // ------------------------------------------------------------------
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [productFamilies, setProductFamilies] = useState([]);
  const [pfMap, setPfMap] = useState({});

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [productFamilyId, setProductFamilyId] = useState("ALL");

  // ------------------------------------------------------------------
  // LOAD PRODUCT FAMILIES
  // ------------------------------------------------------------------
  const loadProductFamilies = useCallback(async () => {
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/product-families"
      );

      const data = res.data?.data || [];
      setProductFamilies(data);

      const map = {};
      data.forEach((pf) => {
        map[pf.id] = pf.name;
      });
      setPfMap(map);
    } catch (err) {
      console.error("Failed to load product families", err);
    }
  }, []);

  // ------------------------------------------------------------------
  // LOAD BOQ MASTERS
  // ------------------------------------------------------------------
  const loadBOQMasters = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};

      if (productFamilyId !== "ALL") {
        params.product_family_id = productFamilyId;
      }

      const res = await axiosClient.get(
        "/api/ppe/orders/boq-master",
        { params }
      );

      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load BOM masters", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [productFamilyId]);

  useEffect(() => {
    loadProductFamilies();
    loadBOQMasters();
  }, [loadProductFamilies, loadBOQMasters]);

  // ------------------------------------------------------------------
  // STATUS DERIVATION (UI ONLY)
  // ------------------------------------------------------------------
  const resolveStatus = (row) => {
  if (!row.is_active) return "INACTIVE";

  if (row.status_code === "SUBMITTED")
    return "SUBMITTED";

  if (row.is_approved)
    return "APPROVED";

  return "DRAFT";
};



  // ------------------------------------------------------------------
  // ACTIONS
  // ------------------------------------------------------------------
  const submitBOQ = async (id) => {
  if (!window.confirm("Submit BOM master for approval?")) return;

  try {
    await axiosClient.post(
      `/api/ppe/orders/boq-masters/${id}/submit`
    );

    // ✅ force reload list
    await loadBOQMasters();

  } catch (err) {
    alert(
      err?.response?.data?.message ||
      "Failed to submit BOM master"
    );
  }
};


  const approveBOQ = async (id) => {
    if (!window.confirm("Approve this BOM master?")) return;
    await axiosClient.post(
      `/api/ppe/orders/boq-masters/${id}/approve`
    );
    loadBOQMasters();
  };

  const rejectBOQ = async (id) => {
    const reason = window.prompt("Enter rejection reason");
    if (!reason) return;

    await axiosClient.post(
      `/api/ppe/orders/boq-masters/${id}/reject`,
      { reason }
    );
    loadBOQMasters();
  };

  const inactivateBOQ = async (id) => {
    if (!window.confirm("Mark this BOM master inactive?")) return;

    await axiosClient.post(
      `/api/ppe/orders/boq-master/${id}/inactive`
    );
    loadBOQMasters();
  };

  // ------------------------------------------------------------------
  // FILTERING
  // ------------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.boq_code?.toLowerCase().includes(q) ||
      r.boq_name?.toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" || resolveStatus(r) === status;

    return matchesSearch && matchesStatus;
  });

  // ------------------------------------------------------------------
  // KPI STATS
  // ------------------------------------------------------------------
  const stats = {
    total: rows.length,
    draft: rows.filter((r) => !r.is_approved && r.is_active).length,
    approved: rows.filter((r) => r.is_approved).length,
    inactive: rows.filter((r) => !r.is_active).length,
  };

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  return (
    <div>
      {/* HEADER */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>BOM Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Engineering Bill of Materials definitions
          </div>
        </div>

        <button
          className="btn btn-light"
          //onClick={() => navigate("/ppe/engineering/boq/create")}
          onClick={() => navigate("/ipe/engineering/bom/create")}
        >
          + Create BOM
        </button>
      </div>

      {/* KPI */}
      <BOMKpiBar stats={stats} />

      {/* FILTERS */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={productFamilyId}
          onChange={(e) => setProductFamilyId(e.target.value)}
        >
          <option value="ALL">All Product Families</option>
          {productFamilies.map((pf) => (
            <option key={pf.id} value={pf.id}>
              {pf.name}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="APPROVED">Approved</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadBOQMasters}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* TABLE */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>BOM Code</th>
                <th>BOM Name</th>
                <th>Product Family</th>
                <th>Version</th>
                <th>Status</th>
                <th style={{ width: 260 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No BOM masters found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => {
                const derivedStatus = resolveStatus(r);

                return (
                  <tr key={r.id}>
                    <td>{r.boq_code || "-"}</td>
                    <td>{r.boq_name}</td>
                    <td>{pfMap[r.product_family_id] || "-"}</td>
                    <td>v{r.version_no}</td>
                    <td>{derivedStatus}</td>

                    <td style={{ display: "flex", gap: 6 }}>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          navigate(`/ppe/engineering/boq/view/${r.id}`)
                        }
                      >
                        View
                      </button>

                      {derivedStatus === "DRAFT" && (
                        <>
                            <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                                navigate(`/ppe/engineering/boq/edit/${r.id}`)
                            }
                            >
                            Edit
                            </button>

                            <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => submitBOQ(r.id)}
                            >
                            Submit
                            </button>
                        </>
                        )}

                        {derivedStatus === "SUBMITTED" && (
                        <>
                            <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => approveBOQ(r.id)}
                            >
                            Approve
                            </button>

                            <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => rejectBOQ(r.id)}
                            >
                            Reject
                            </button>
                        </>
                        )}

                        {derivedStatus === "APPROVED" && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => inactivateBOQ(r.id)}
                        >
                            Inactivate
                        </button>
                        )}

                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
